// The mnemonic is your identity. Lose it and you lose your account — there's
// no recovery email, no support ticket, no admin who can bail you out.
// That's the point. Bitcoin figured this out first: if you want something
// nobody can take from you, you have to be the only one who holds the key.
//
// We use BIP-39 here for the same reason Bitcoin uses it: it's a well-audited
// spec, the wordlist is human-readable, and 128 bits of entropy is enough
// that brute-forcing it isn't a realistic attack. Twelve words, one phrase,
// one identity.
//
// The implementation uses Web Crypto throughout:
//   - `crypto.getRandomValues` for entropy in `generateMnemonic`
//   - SHA-256 (`subtle.digest`) for the BIP-39 checksum
//   - PBKDF2-HMAC-SHA512 (`subtle.deriveBits`) for `mnemonicToSeed`
//
// Because Web Crypto's SHA-256 is async, `generateMnemonic` and
// `validateMnemonic` are async too. The BIP-39 spec sketches synchronous
// shapes but "no hand-rolled crypto" wins here — a tiny API awkwardness
// is worth it to avoid reimplementing SHA-256 by hand.

import { MNEMONIC_WORD_COUNT } from "@link/shared/api/constants";

import {
  BIP39_WORDLIST_ENGLISH,
  BIP39_WORDLIST_INDEX,
  BIP39_WORDLIST_SIZE,
} from "./wordlist-english";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Entropy size in bits for a 12-word BIP-39 mnemonic. (BIP-39 §3:
 * `ENT = 128` for 12 words; `CS = ENT / 32 = 4` checksum bits;
 * `MS = (ENT + CS) / 11 = 12` words.)
 */
const ENTROPY_BITS = 128;
const ENTROPY_BYTES = ENTROPY_BITS / 8; // 16
const CHECKSUM_BITS = ENTROPY_BITS / 32; // 4
const TOTAL_BITS = ENTROPY_BITS + CHECKSUM_BITS; // 132
const BITS_PER_WORD = 11;

/** PBKDF2 iteration count mandated by BIP-39 §6. */
const PBKDF2_ITERATIONS = 2048;
/** PBKDF2 output length in bytes mandated by BIP-39 §6 (64 bytes / 512 bits). */
const PBKDF2_KEY_LEN_BYTES = 64;

// Sanity check — if someone ever edits the shared constant to support 24-word
// phrases without also updating this module, we catch it at startup rather
// than silently generating the wrong number of words.
if (MNEMONIC_WORD_COUNT !== TOTAL_BITS / BITS_PER_WORD) {
  throw new Error(
    `BIP-39 wordcount mismatch: shared MNEMONIC_WORD_COUNT=${MNEMONIC_WORD_COUNT} ` +
      `but this module is wired for ${TOTAL_BITS / BITS_PER_WORD} words.`,
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Discriminated result of `validateMnemonic`. We return a specific kind
 * instead of a plain boolean so the UI can show the right error — "that word
 * doesn't exist" is a different UX from "those words are valid but the
 * checksum is wrong".
 *
 * - `unknown_word`: at least one word isn't in the BIP-39 English wordlist.
 *   The first bad word is in `word` so the UI can highlight it.
 * - `bad_checksum`: every word is valid but the trailing checksum bits don't
 *   match SHA-256(entropy). Usually means a typo somewhere in the phrase.
 * - `bad_word_count`: phrase didn't have exactly 12 words.
 */
export type ValidateMnemonicResult =
  | { ok: true }
  | { ok: false; kind: "unknown_word"; word: string }
  | { ok: false; kind: "bad_checksum" }
  | { ok: false; kind: "bad_word_count"; count: number };

/**
 * Generate a fresh 12-word BIP-39 English mnemonic with a valid checksum.
 *
 * Entropy comes from `crypto.getRandomValues` — we never touch the entropy,
 * the intermediate seed, or the phrase after returning it. What the caller
 * does with the string is their problem.
 */
export async function generateMnemonic(): Promise<string> {
  const entropy = new Uint8Array(ENTROPY_BYTES);
  // Web Crypto's CSPRNG. Throws if the runtime lacks Web Crypto.
  crypto.getRandomValues(entropy);
  return entropyToMnemonic(entropy);
}

/**
 * Validate a candidate 12-word mnemonic against the BIP-39 English wordlist
 * and checksum. We canonicalize first (trim, lowercase, collapse whitespace)
 * so copy-paste artifacts don't cause spurious failures.
 *
 * Returns a discriminated result so the UI can show a specific error rather
 * than a generic "invalid phrase". Never throws on malformed input.
 */
export async function validateMnemonic(
  phrase: string,
): Promise<ValidateMnemonicResult> {
  const words = canonicalizeMnemonic(phrase).split(" ").filter(Boolean);

  if (words.length !== MNEMONIC_WORD_COUNT) {
    return { ok: false, kind: "bad_word_count", count: words.length };
  }

  // First pass: every word must be in the wordlist. We surface the first
  // unknown word so the UI can highlight it.
  const indices: number[] = new Array(words.length);
  for (let i = 0; i < words.length; i++) {
    const idx = BIP39_WORDLIST_INDEX.get(words[i]!);
    if (idx === undefined) {
      return { ok: false, kind: "unknown_word", word: words[i]! };
    }
    indices[i] = idx;
  }

  // Second pass: reconstruct entropy + checksum bits and verify.
  const allBits = indicesToBits(indices, BITS_PER_WORD);
  const entropyBits = allBits.subarray(0, ENTROPY_BITS);
  const givenChecksumBits = allBits.subarray(ENTROPY_BITS);
  const entropy = bitsToBytes(entropyBits);
  const expectedChecksumBits = await sha256ChecksumBits(entropy, CHECKSUM_BITS);

  if (!bitsEqual(givenChecksumBits, expectedChecksumBits)) {
    return { ok: false, kind: "bad_checksum" };
  }

  return { ok: true };
}

/**
 * Derive the BIP-39 64-byte seed from a mnemonic phrase using
 * PBKDF2-HMAC-SHA512 with 2048 iterations, per BIP-39 §6.
 *
 * We do NFKD normalization before PBKDF2 because BIP-39 requires it, and
 * canonicalize first so this agrees with `validateMnemonic` on whitespace
 * and casing. The passphrase is optional — v1 doesn't use one, but the
 * argument is here for forward compatibility.
 *
 * @returns exactly 64 bytes. Any further key derivation is the caller's job.
 */
export async function mnemonicToSeed(
  phrase: string,
  passphrase = "",
): Promise<Uint8Array> {
  // Canonicalize then NFKD-normalize. We do canonicalization so
  // mnemonicToSeed agrees with validateMnemonic on whitespace/casing,
  // and NFKD is required by BIP-39 §6.
  const canonical = canonicalizeMnemonic(phrase);
  const passwordBytes = utf8(canonical.normalize("NFKD"));
  const saltBytes = utf8(("mnemonic" + passphrase).normalize("NFKD"));

  const baseKey = await crypto.subtle.importKey(
    "raw",
    passwordBytes,
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );

  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-512",
    },
    baseKey,
    PBKDF2_KEY_LEN_BYTES * 8,
  );

  return new Uint8Array(bits);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Canonicalize a candidate phrase: trim, lowercase, collapse whitespace. */
function canonicalizeMnemonic(phrase: string): string {
  return phrase.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

/**
 * Encode a known-valid 16-byte entropy buffer into a 12-word BIP-39
 * mnemonic. Also used to recover an encrypted emergency backup on-device.
 */
export async function entropyToMnemonic(entropy: Uint8Array): Promise<string> {
  if (entropy.length !== ENTROPY_BYTES) {
    throw new Error(
      `entropyToMnemonic: expected ${ENTROPY_BYTES} bytes of entropy, got ${entropy.length}`,
    );
  }
  const checksumBits = await sha256ChecksumBits(entropy, CHECKSUM_BITS);
  const entropyBits = bytesToBits(entropy);
  const allBits = new Uint8Array(TOTAL_BITS);
  allBits.set(entropyBits, 0);
  allBits.set(checksumBits, ENTROPY_BITS);

  const words: string[] = new Array(MNEMONIC_WORD_COUNT);
  for (let i = 0; i < MNEMONIC_WORD_COUNT; i++) {
    let idx = 0;
    for (let b = 0; b < BITS_PER_WORD; b++) {
      idx = (idx << 1) | allBits[i * BITS_PER_WORD + b]!;
    }
    if (idx < 0 || idx >= BIP39_WORDLIST_SIZE) {
      // Can't happen given a well-formed bit array, but better than a
      // silent out-of-bounds read.
      throw new Error(`Internal error: word index ${idx} out of range`);
    }
    words[i] = BIP39_WORDLIST_ENGLISH[idx]!;
  }
  return words.join(" ");
}

/** Extract validated BIP-39 entropy. Caller must wipe the returned buffer. */
export async function mnemonicToEntropy(phrase: string): Promise<Uint8Array> {
  if (!(await validateMnemonic(phrase)).ok) throw new Error("Invalid recovery phrase");
  const indices = canonicalizeMnemonic(phrase).split(" ").map(word => BIP39_WORDLIST_INDEX.get(word)!);
  return bitsToBytes(indicesToBits(indices, BITS_PER_WORD).subarray(0, ENTROPY_BITS));
}

/**
 * Compute the first `n` checksum bits of SHA-256(entropy), MSB-first.
 * For a 12-word phrase that's 4 bits (entropy_bits / 32).
 */
async function sha256ChecksumBits(
  entropy: Uint8Array,
  n: number,
): Promise<Uint8Array> {
  const digest = new Uint8Array(
    await crypto.subtle.digest("SHA-256", entropy),
  );
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const byte = digest[i >> 3]!;
    out[i] = (byte >> (7 - (i & 7))) & 1;
  }
  return out;
}

/** Expand a byte array to its big-endian bit array (MSB first). */
function bytesToBits(bytes: Uint8Array): Uint8Array {
  const bits = new Uint8Array(bytes.length * 8);
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i]!;
    for (let b = 0; b < 8; b++) {
      bits[i * 8 + b] = (byte >> (7 - b)) & 1;
    }
  }
  return bits;
}

/** Pack a big-endian bit array (MSB first) back into bytes. */
function bitsToBytes(bits: Uint8Array): Uint8Array {
  if (bits.length % 8 !== 0) {
    throw new Error(`bitsToBytes: length ${bits.length} is not a multiple of 8`);
  }
  const bytes = new Uint8Array(bits.length / 8);
  for (let i = 0; i < bytes.length; i++) {
    let byte = 0;
    for (let b = 0; b < 8; b++) {
      byte = (byte << 1) | bits[i * 8 + b]!;
    }
    bytes[i] = byte;
  }
  return bytes;
}

/**
 * Convert each `width`-bit index into its big-endian bits and concatenate.
 * For BIP-39, `width = 11` and indices are 0..2047.
 */
function indicesToBits(indices: number[], width: number): Uint8Array {
  const bits = new Uint8Array(indices.length * width);
  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i]!;
    for (let b = 0; b < width; b++) {
      bits[i * width + b] = (idx >> (width - 1 - b)) & 1;
    }
  }
  return bits;
}

/** Constant-length bit-array equality. Not constant-time on purpose: a
 * checksum mismatch is not secret material. */
function bitsEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const TEXT_ENCODER = new TextEncoder();
function utf8(s: string): Uint8Array {
  return TEXT_ENCODER.encode(s);
}

// Re-export the wordlist module so callers that need it (e.g. the
// signup-confirmation UI in apps/web) don't have to deep-import.
export {
  BIP39_WORDLIST_ENGLISH,
  BIP39_WORDLIST_INDEX,
  BIP39_WORDLIST_SIZE,
} from "./wordlist-english";
